import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import React, { useEffect, useState } from 'react'
import { Grid, Stack, Tooltip } from '@mui/material'
import { template } from 'lodash'
import { nbPerPageLabel, SANDRE } from '../../../../../referencial/constants/ReferencialConstants'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import { StyledFieldSet, StyledLegend, StyledSpan } from '../../../../../components/StyledElements'
import { getSandreLabel } from '../../../../../utils/StringUtil'
import ReferencialAction from '../../../../../referencial/action/ReferencialAction'
import { getObjectLabel } from '../../../../../utils/StoreUtils'
import JobAction from '../../../../../import/actions/JobAction'
import { JOB_TYPES } from '../AddPiezoConstants'
import { getJobList } from '../../../../../utils/JobUtils'
import Input from '../../../../../components/forms/Input'
import Select from '../../../../../components/forms/Select'
import { hasValue } from '../../../../../utils/NumberUtil'
import Button from '../../../../../components/forms/Button'
import ToastrAction from '../../../../../toastr/actions/ToastrAction'
import { push } from '@lagunovsky/redux-react-router'
import { ALIGN, CardTable } from 'components/datatable/NewTable'
import AndroidIcon from '@mui/icons-material/Android'


const getNumberList = max => [...Array(max)].map((_, i) => ({ value: `${i}`, label: i > 9 ? i : `0${i}` }))
const getHours = () => [ { value: '*', label: i18n.everyHours }, ...getNumberList(24) ]
const getMinutes = () => [
    { value: '*/5', label: template(i18n.everyCustomMinutes)({ minute: 5 }) },
    { value: '*/10', label: template(i18n.everyCustomMinutes)({ minute: 10 }) },
    { value: '*/15', label: template(i18n.everyCustomMinutes)({ minute: 15 }) },
    { value: '*/20', label: template(i18n.everyCustomMinutes)({ minute: 20 }) },
    { value: '*/30', label: template(i18n.everyCustomMinutes)({ minute: 30 }) },
    ...getNumberList(60),
]
const getDays = () => [
    { value: '*', label: i18n.allDays },
    { value: '1', label: i18n.monday },
    { value: '2', label: i18n.tuesday },
    { value: '3', label: i18n.wednesday, disabled: true },
    { value: '4', label: i18n.thursday },
    { value: '5', label: i18n.friday },
    { value: '6', label: i18n.saturday },
    { value: '0', label: i18n.sunday },
]

const changePlanification = ({ minutes, hours, days }, changeJob) => {
    const cron = ['0', minutes, hours, '*', '*', days, '*'].join(' ')
    changeJob({ cron, minutes, hours, days })
}

const handleChangeTime = (k, v, job, changeJob) => {
    if (k === 'minutes' && v.includes('/')) {
        changePlanification({ ...job, minutes: v, hours: '*', days: '*' }, changeJob)
    } else if (k === 'hours' && v.includes('/')) {
        changePlanification({ ...job, hours: v, days: '*' }, changeJob)
    } else {
        changePlanification({ ...job, [k]: v }, changeJob)
    }
}

const handleChangeDays = (selected, job, changeJob) => {
    changePlanification({ ...job, days: hasValue(selected) && selected != -1 ? selected.toString() : '*' }, changeJob)
}

const addJob = (newJob, setNewJob, initialJob, dispatch, jobTableCriterias, setJobsWithStation, code) => {
    if (!hasValue(newJob.name)) {
        dispatch(ToastrAction.warning(i18n.pleaseCompleteAllField))
    } else {
        dispatch(JobAction.addJob(newJob, false)).then(() => {
            setNewJob(initialJob)
            dispatch(JobAction.fetchJobTable(jobTableCriterias))
            JobAction.jobHasStation(code).then(json => setJobsWithStation(json))
        })
    }
}


const AddPiezoStepJob = ({
    station,
}) => {
    const dispatch = useDispatch()

    const {
        piezometers,
        sandreCodes,
        citiesIndex,
        jobTable,
    } = useSelector(store => ({
        piezometers: store.PiezometryReducer.piezometerListSpecific,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        citiesIndex: store.CityReducer.citiesIndex,
        jobTable: store.JobReducer.jobTable,
    }), shallowEqual)

    const jobTableCriterias = {
        jobType: JOB_TYPES[station.stationType],
        forceLoad: true,
        scheduled: true,
        displayMode: 'job',
    }
    const initialJob = { ...station.job, cron: '0 0 0 * * * *', days: '*', hours: '0', minutes: '0', id: null, name: null }

    const [newJob, setNewJob] = useState(initialJob)
    const [jobsWithStation, setJobsWithStation] = useState([])
    const changeJob = (changes) => setNewJob({ ...newJob, ...changes })

    useEffect(() => {
        if (!sandreCodes.length) {
            dispatch(ReferencialAction.fetchSandreCodes())
        }
        dispatch(JobAction.fetchJobTable(jobTableCriterias))
        JobAction.jobHasStation(station.code).then(json => setJobsWithStation(json))
    }, [])

    const stations = station.ids.map(i => piezometers.find(q => q.id === i)).map(s => ({
        id: s.id,
        code: { value: s.code },
        name: { value: s.name },
        nature: { value: getSandreLabel(sandreCodes, SANDRE.PIEZOMETER_NATURE, station.nature) },
        city: { value: getObjectLabel(citiesIndex[s.townCode], 'labelWithCode') },
    }))

    const jobList = getJobList(jobTable.data).jobs.map(j => {
        const jobHasStation = jobsWithStation.some(job => j.id === job.id)
        const { programmation, ...rest } = j
        return {
            ...rest,
            programmation: {
                value: (
                    <Stack spacing={1} direction='row'>
                        <Tooltip title={i18n.jobScheduled}>
                            <AndroidIcon/>
                        </Tooltip>
                        {programmation.value}
                    </Stack>
                ),
                sortValue: programmation.value,
            },
            lastExecution: { value: j.date.value },
            jobHasStation,
        }
    })

    return (
        <Stack spacing={2}>
            <CardTable
                title={i18n.importedData}
                rows={stations}
                lineActions={[
                    {
                        align: ALIGN.RIGHT,
                        icon: 'border_color',
                        color: '#2196F3',
                        tooltip: i18n.accessDescription,
                        onClick: ({ id }) => dispatch(push(`/station/piezometry/${id}/description`)),
                    },
                    {
                        align: ALIGN.RIGHT,
                        icon: 'arrow_forward',
                        color: '#2196F3',
                        tooltip: i18n.accessToTheFile,
                        onClick: ({ id }) => dispatch(push(`/station/piezometry/${id}/dashboard`)),
                    },
                ]}
                headers={['code', 'name', 'city', 'nature']}
                maxHeight='62vh'
            />
            <CardTable
                title={i18n.jobs}
                rows={jobList}
                headers={['jobName', 'status', 'lastExecution', 'programmation']}
                maxHeight='62vh'
                rowsPerPageOptions={nbPerPageLabel}
                lineActions={[
                    {
                        icon: ({ jobHasStation }) => jobHasStation ? 'location_on' : 'location_off',
                        align: ALIGN.RIGHT,
                        color: 'rgba(0, 0, 0, 0.87)',
                        tooltip: ({ jobHasStation }) => jobHasStation ? i18n.stationIsIn : i18n.stationIsOut,
                    },
                    {
                        icon: ({ jobHasStation }) => jobHasStation ? 'clear' : 'note_add',
                        align: ALIGN.RIGHT,
                        color: '#2196F3',
                        tooltip: ({ jobHasStation }) => jobHasStation ? i18n.removeStationFromJob : i18n.addStationToJob,
                        onClick: ({ jobHasStation, id }) => jobHasStation ?
                            JobAction.removeStationFromJob(station.code, id).then(() => JobAction.jobHasStation(station.code).then(json => {
                                setJobsWithStation(json)
                                dispatch(ToastrAction.success(i18n.stationHasBeenRemoved))
                            }))
                            :
                            JobAction.addStationToJob(station.code, id).then(() => JobAction.jobHasStation(station.code).then(json => {
                                setJobsWithStation(json)
                                dispatch(ToastrAction.success(i18n.stationHasBeenAdd))
                            })),
                    },
                ]}
            />
            <StyledFieldSet>
                <StyledLegend>
                    <StyledSpan>{i18n.addJob}</StyledSpan>
                </StyledLegend>
                <Grid container spacing={1}>
                    <Grid size={12}>
                        <Input title={i18n.name} value={newJob.name} onChange={v => changeJob({ name: v })} />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            value={ newJob.hours || 0 }
                            options={ getHours() }
                            label={ i18n.jobHour }
                            onChange={ v => handleChangeTime('hours', v, newJob, changeJob) }
                            noNullValue
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            value={ newJob.minutes || 0 }
                            options={ getMinutes() }
                            label={ i18n.jobMinute }
                            onChange={ v => handleChangeTime('minutes', v, newJob, changeJob) }
                            noNullValue
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            value={ newJob.days }
                            options={ getDays() }
                            label={ i18n.jobDay }
                            onChange={ v => handleChangeDays(v, newJob, changeJob) }
                            noNullValue
                        />
                    </Grid>
                    <Grid container size={3} justifyContent='center' alignItems='center'>
                        <Button title={i18n.add} icon='note_add' onClick={() => addJob(newJob, setNewJob, initialJob, dispatch, jobTableCriterias, setJobsWithStation, station.code)}/>
                    </Grid>
                </Grid>
            </StyledFieldSet>
        </Stack>
    )
}

AddPiezoStepJob.propTypes = {
    station: PropTypes.shape({
        code: PropTypes.string,
        stationType: PropTypes.string,
        name: PropTypes.string,
        jobExecutionId: PropTypes.number,
        job: PropTypes.shape({
            id: PropTypes.number,
        }),
        jobExecution: PropTypes.shape({
            statusCode: PropTypes.number,
        }),
        ids: PropTypes.arrayOf(PropTypes.number),
        nature: PropTypes.string,
    }),
}

export default AddPiezoStepJob